import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Shield,
  CheckCircle,
  Clock,
  AlertCircle,
  ChevronRight,
  Loader,
  RefreshCw,
} from 'lucide-react';
import securityApi from '../../api/securityApi';
import toast from 'react-hot-toast';

const ComplianceProjectsList = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch projects on mount
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await securityApi.listComplianceProjects();
      setProjects(response.projects || []);
    } catch (err) {
      console.error('Error fetching compliance projects:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
            <CheckCircle className="w-3 h-3" />
            Completed
          </span>
        );
      case 'in_progress':
        return (
          <span className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
            <Clock className="w-3 h-3" />
            In Progress
          </span>
        );
      case 'not_started':
        return (
          <span className="flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
            <Clock className="w-3 h-3" />
            Not Started
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-teal-100 rounded-lg">
            <Shield className="w-8 h-8 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Compliance Projects</h1>
            <p className="text-sm text-gray-500">
              Manage your SOC 2 and compliance assessments
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchProjects}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => navigate('/compliance/new')}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            New Project
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!error && projects.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-teal-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No Compliance Projects Yet
          </h2>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Start your first compliance assessment to track your SOC 2 readiness
            and identify gaps in your security controls.
          </p>
          <button
            onClick={() => navigate('/compliance/new')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Create Your First Project
          </button>
        </div>
      )}

      {/* Projects List */}
      {projects.length > 0 && (
        <div className="space-y-4">
          {projects.map((project) => {
            // Use progress_percentage from API for consistency with dashboard
            const progress = Math.round(project.progress_percentage || 0);

            return (
              <div
                key={project.id}
                onClick={() => navigate(`/compliance/projects/${project.id}`)}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:border-teal-300 hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {project.name}
                      </h3>
                      {getStatusBadge(project.status)}
                    </div>

                    {/* Connected Integrations */}
                    {project.connected_integrations && project.connected_integrations.length > 0 && (
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs text-gray-500">Integrations:</span>
                        {project.connected_integrations.map((integration) => (
                          <span
                            key={integration}
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              integration === 'aws'
                                ? 'bg-orange-100 text-orange-700'
                                : integration === 'google_workspace'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {integration === 'aws' ? 'AWS' :
                             integration === 'google_workspace' ? 'Google Workspace' :
                             integration === 'github' ? 'GitHub' : integration}
                          </span>
                        ))}
                      </div>
                    )}

                    {project.description && (
                      <p className="text-sm text-gray-500 mb-3">
                        {project.description}
                      </p>
                    )}

                    <div className="flex items-center gap-6 text-sm">
                      <span className="text-gray-600">
                        <span className="font-medium">{project.framework_name || 'SOC 2 Type I'}</span>
                      </span>
                      <span className="text-gray-600">
                        <span className="font-medium">{project.completed_tasks || 0}</span>
                        <span className="text-gray-400"> / {project.total_tasks || 0} tasks</span>
                      </span>
                      {project.target_date && (
                        <span className="text-gray-600">
                          Target: <span className="font-medium">{new Date(project.target_date).toLocaleDateString()}</span>
                        </span>
                      )}
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500">Progress</span>
                        <span className="text-xs font-medium text-gray-700">{progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-500 ${
                            progress >= 100
                              ? 'bg-green-500'
                              : progress >= 50
                                ? 'bg-teal-500'
                                : 'bg-amber-500'
                          }`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  <ChevronRight className="w-5 h-5 text-gray-400 ml-4 flex-shrink-0" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ComplianceProjectsList;
